import { creating, exist, Id } from '@campaign-portal/namespace/common/id';
import { DecisionMode, SubscriptionType, TrafficPurpose, TrafficType } from '@campaign-portal/namespace/common/enums';
import {
	Package,
	PackRecord,
	PackSettings,
	Subscription,
} from '@campaign-portal/namespace/entities/subscriptions/specs';
import { ViberSettings } from '@campaign-portal/namespace/common/viberSettings';
import { SubscriptionGroup } from '@campaign-portal/namespace/entities/subscription-groups/specs';

export interface ProductEntityConfig<ID = exist | creating> extends Partial<Subscription<ID>> {
	id: ID;
	currencyId: Id<exist>;
	viberSettings?: ViberSettings;
}

export class ProductEntity<ID = exist | creating> implements Subscription<ID> {
	[key: string]: unknown;

	id: ID;
	type: SubscriptionType;
	name: string;
	trafficType: TrafficType;
	currencyId: Id<exist>;
	viberSettings?: ViberSettings;
	billingMode: DecisionMode;
	groupId?: Id<exist>;
	countries?: string[];
	description?: string;
	isActive?: boolean;
	partners?: { id: Id<exist>; subscriptionDate: string; recordId?: Id<exist> }[];
	contractCompanies?: Id<exist>[];
	personalFor?: Id<exist>[];
	isOld?: boolean;

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: ProductEntityConfig<ID>) {
		this.id = config.id;
		this.currencyId = config.currencyId;
		this.type = config.type ?? SubscriptionType.VENDOR;
		this.name = config.name ?? '';
		this.trafficType = config.trafficType ?? TrafficType.SMS;
		this.billingMode = config.billingMode ?? DecisionMode.BY_DELIVERED;
		this.viberSettings = config.viberSettings;
		this.groupId = config.groupId;
		this.countries = config.countries;
		this.description = config.description;
		this.isActive = config.isActive;
		this.partners = config.partners;
		this.contractCompanies = config.contractCompanies;
		this.personalFor = config.personalFor;
		this.isOld = !!config.isOld;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}
}

export interface PackageEntityConfig<ID = exist | creating> extends Partial<Package<ID>> {
	id: ID;
}

export class PackageEntity<ID = exist | creating> implements Package<ID> {
	[key: string]: unknown;

	id: ID;
	name: string;
	type: SubscriptionType.PACK;
	trafficType: TrafficType;
	packSettings: PackSettings;
	messagesTotal: number;
	billingMode: DecisionMode;
	availableCountryNetList?: Id<exist>[] | null;

	currencyId?: Id<exist>;
	groupId?: Id<exist>;
	countries?: string[];
	description?: string;
	isActive?: boolean;
	contractCompanies?: Id<exist>[];
	personalFor?: Id<exist>[];
	partners?: { id: Id<exist>; subscriptionDate: string; recordId?: Id<exist> }[];

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: PackageEntityConfig<ID>) {
		this.id = config.id;
		this.availableCountryNetList = config.availableCountryNetList ?? [];
		this.name = config.name ?? '';
		this.type = config.type ?? SubscriptionType.PACK;
		this.trafficType = config.trafficType ?? TrafficType.SMS;
		this.packSettings = config.packSettings ?? {
			activeFrom: new Date().toISOString(),
			activeTo: new Date('2100').toISOString(),
			packPrice: 0,
			isPromo: false,
			validityPeriodByDay: 31,
			trafficPurpose: TrafficPurpose.PROMOTION,
		};
		this.messagesTotal = config.messagesTotal ?? 0;
		this.billingMode = config.billingMode ?? DecisionMode.BY_DELIVERED;
		this.currencyId = config.currencyId;
		this.countries = config.countries;
		this.description = config.description;
		this.isActive = config.isActive;
		this.partners = config.partners;
		this.contractCompanies = config.contractCompanies;
		this.groupId = config.groupId;
		this.personalFor = config.personalFor;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}
}

export interface PackRecordEntityConfig<ID = exist | creating> extends Partial<PackRecord<ID>> {
	id: ID;
	subscriptionId: Id<exist>;
}

export class PackRecordEntity<ID = exist | creating> implements PackRecord<ID> {
	id: ID;
	subscriptionId: Id<exist>;
	activeFrom?: string;
	activeTo?: string;
	messagesUsed?: number;
	messagesLocked?: number;

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: PackRecordEntityConfig<ID>) {
		this.id = config.id;
		this.subscriptionId = config.subscriptionId;
		this.activeFrom = config.activeFrom;
		this.activeTo = config.activeTo;
		this.messagesUsed = config.messagesUsed;
		this.messagesLocked = config.messagesLocked;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}
}

export interface SubscriptionGroupEntityConfig<ID = exist | creating> extends Partial<SubscriptionGroup<ID>> {
	id: ID;
}

export class SubscriptionGroupEntity<ID = exist | creating> implements SubscriptionGroup<ID> {
	id: ID;
	name: string;
	plans: Id<exist>[];
	packs: Id<exist>[];
	allowRoutingOutside: boolean;
	description?: string;

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: SubscriptionGroupEntityConfig<ID>) {
		this.id = config.id;
		this.name = config.name ?? '';
		this.plans = config.plans ?? [];
		this.packs = config.packs ?? [];
		this.allowRoutingOutside = config.allowRoutingOutside ?? false;
		this.description = config.description;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}

	isEmpty(): boolean {
		return this.plans.length === 0 && this.packs.length === 0;
	}
}
